/** Volume for money-flow style indicators; defaults when column missing */
export const candleVolume = (c) => {
  const v = c?.volume;
  return Number.isFinite(v) && v > 0 ? v : 1;
};

/** True when uploaded data includes real volume (not only synthetic default). */
export const dataHasRealVolume = (candles) =>
  Array.isArray(candles) &&
  candles.some((c) => Number.isFinite(c?.volume) && c.volume > 0 && c.volume !== 1);

const typicalPrice = (c) => (c.high + c.low + c.close) / 3;

export const calculateSMA = (candles, period) => {
  if (!candles.length || period < 1) return [];
  let rollingSum = 0;
  return candles.flatMap((candle, index) => {
    rollingSum += candle.close;
    if (index >= period) rollingSum -= candles[index - period].close;
    if (index < period - 1) return [];
    return [{ time: candle.time, value: rollingSum / period }];
  });
};

/**
 * Simple moving average of an arbitrary { time, value } series — same rolling-sum SMA as
 * calculateSMA, but driven off `value` instead of candle close. Lets any indicator's
 * output (MM Stat, RSI, MACD line, …) be smoothed the same way price is.
 */
export const calculateSMAOfSeries = (seriesPoints, period) => {
  if (!seriesPoints || !seriesPoints.length || period < 1) return [];
  let rollingSum = 0;
  return seriesPoints.flatMap((point, index) => {
    rollingSum += point.value;
    if (index >= period) rollingSum -= seriesPoints[index - period].value;
    if (index < period - 1) return [];
    return [{ time: point.time, value: rollingSum / period }];
  });
};

export const calculateEMA = (candles, period) => {
  if (!candles.length || period < 1) return [];
  const smoothing = 2 / (period + 1);
  let emaValue = candles[0].close;
  return candles.map((candle, index) => {
    if (index === 0) return { time: candle.time, value: emaValue };
    emaValue = candle.close * smoothing + emaValue * (1 - smoothing);
    return { time: candle.time, value: emaValue };
  });
};

export const calculateWMA = (candles, period) => {
  if (!candles.length || period < 1) return [];
  const weightSum = (period * (period + 1)) / 2;
  return candles.flatMap((candle, index) => {
    if (index < period - 1) return [];
    const weightedTotal = candles
      .slice(index - period + 1, index + 1)
      .reduce((sum, item, windowIndex) => sum + item.close * (windowIndex + 1), 0);
    return [{ time: candle.time, value: weightedTotal / weightSum }];
  });
};

export const calculateBollingerBands = (candles, period, deviation = 2) => {
  if (!candles.length || period < 1) return { upper: [], middle: [], lower: [] };
  const upper = [];
  const middle = [];
  const lower = [];
  for (let index = period - 1; index < candles.length; index += 1) {
    const window = candles.slice(index - period + 1, index + 1);
    const mean = window.reduce((sum, candle) => sum + candle.close, 0) / period;
    const variance =
      window.reduce((sum, candle) => sum + (candle.close - mean) ** 2, 0) / period;
    const stdDev = Math.sqrt(variance);
    const time = candles[index].time;
    upper.push({ time, value: mean + deviation * stdDev });
    middle.push({ time, value: mean });
    lower.push({ time, value: mean - deviation * stdDev });
  }
  return { upper, middle, lower };
};

export const calculateRSI = (candles, period) => {
  if (candles.length <= period || period < 1) return [];
  let gainSum = 0;
  let lossSum = 0;
  for (let index = 1; index <= period; index += 1) {
    const change = candles[index].close - candles[index - 1].close;
    gainSum += Math.max(change, 0);
    lossSum += Math.max(-change, 0);
  }
  let averageGain = gainSum / period;
  let averageLoss = lossSum / period;
  const rsiData = [];
  for (let index = period; index < candles.length; index += 1) {
    if (index > period) {
      const change = candles[index].close - candles[index - 1].close;
      const gain = Math.max(change, 0);
      const loss = Math.max(-change, 0);
      averageGain = (averageGain * (period - 1) + gain) / period;
      averageLoss = (averageLoss * (period - 1) + loss) / period;
    }
    const rs = averageLoss === 0 ? 100 : averageGain / averageLoss;
    const rsi = 100 - 100 / (1 + rs);
    rsiData.push({
      time: candles[index].time,
      value: Number.isFinite(rsi) ? rsi : 50,
    });
  }
  return rsiData;
};

export const calculateATR = (candles, period) => {
  if (candles.length <= period || period < 1) return [];
  const trueRanges = candles.map((candle, index) => {
    if (index === 0) return candle.high - candle.low;
    const previousClose = candles[index - 1].close;
    return Math.max(
      candle.high - candle.low,
      Math.abs(candle.high - previousClose),
      Math.abs(candle.low - previousClose)
    );
  });
  let atr = trueRanges.slice(1, period + 1).reduce((sum, value) => sum + value, 0) / period;
  const atrData = [{ time: candles[period].time, value: atr }];
  for (let index = period + 1; index < candles.length; index += 1) {
    atr = (atr * (period - 1) + trueRanges[index]) / period;
    atrData.push({ time: candles[index].time, value: atr });
  }
  return atrData;
};

export const calculateDonchianChannels = (candles, period) => {
  if (!candles.length || period < 1) return { upper: [], middle: [], lower: [] };
  const upper = [];
  const middle = [];
  const lower = [];
  for (let index = period - 1; index < candles.length; index += 1) {
    const window = candles.slice(index - period + 1, index + 1);
    const highestHigh = Math.max(...window.map((candle) => candle.high));
    const lowestLow = Math.min(...window.map((candle) => candle.low));
    const time = candles[index].time;
    upper.push({ time, value: highestHigh });
    middle.push({ time, value: (highestHigh + lowestLow) / 2 });
    lower.push({ time, value: lowestLow });
  }
  return { upper, middle, lower };
};

export const calculateStochastic = (candles, kPeriod, dPeriod) => {
  if (candles.length < kPeriod || kPeriod < 1 || dPeriod < 1) {
    return { k: [], d: [] };
  }
  const kValues = [];
  for (let index = kPeriod - 1; index < candles.length; index += 1) {
    const window = candles.slice(index - kPeriod + 1, index + 1);
    const highestHigh = Math.max(...window.map((candle) => candle.high));
    const lowestLow = Math.min(...window.map((candle) => candle.low));
    const denominator = highestHigh - lowestLow;
    const value =
      denominator === 0 ? 50 : ((candles[index].close - lowestLow) / denominator) * 100;
    kValues.push({ time: candles[index].time, value });
  }
  const dValues = kValues.flatMap((entry, index) => {
    if (index < dPeriod - 1) return [];
    const window = kValues.slice(index - dPeriod + 1, index + 1);
    return [
      {
        time: entry.time,
        value: window.reduce((sum, item) => sum + item.value, 0) / dPeriod,
      },
    ];
  });
  return { k: kValues, d: dValues };
};

export const calculateMACD = (candles, fastPeriod, slowPeriod, signalPeriod) => {
  if (
    !candles.length ||
    fastPeriod < 1 ||
    slowPeriod < 1 ||
    signalPeriod < 1 ||
    fastPeriod >= slowPeriod
  ) {
    return { line: [], signal: [], histogram: [] };
  }
  const fastEma = calculateEMA(candles, fastPeriod);
  const slowEma = calculateEMA(candles, slowPeriod);
  const slowEmaByTime = new Map(slowEma.map((entry) => [entry.time, entry.value]));
  const macdLine = fastEma.flatMap((entry) => {
    const slowValue = slowEmaByTime.get(entry.time);
    if (!Number.isFinite(slowValue)) return [];
    return [{ time: entry.time, value: entry.value - slowValue }];
  });
  if (macdLine.length < signalPeriod) {
    return { line: macdLine, signal: [], histogram: [] };
  }
  const signalLine = calculateEMA(
    macdLine.map((entry) => ({ time: entry.time, close: entry.value })),
    signalPeriod
  );
  const signalByTime = new Map(signalLine.map((entry) => [entry.time, entry.value]));
  const histogram = macdLine.flatMap((entry) => {
    const signalValue = signalByTime.get(entry.time);
    if (!Number.isFinite(signalValue)) return [];
    const value = entry.value - signalValue;
    return [
      {
        time: entry.time,
        value,
        color: value >= 0 ? "#16a34a" : "#dc2626",
      },
    ];
  });
  return { line: macdLine, signal: signalLine, histogram };
};

const emaOfSeries = (series, period) => {
  if (!series.length || period < 1) return [];
  const k = 2 / (period + 1);
  const out = [];
  let ema = series[0].close;
  out.push({ time: series[0].time, value: ema });
  for (let i = 1; i < series.length; i += 1) {
    ema = series[i].close * k + ema * (1 - k);
    out.push({ time: series[i].time, value: ema });
  }
  return out;
};

export const calculateCCI = (candles, period) => {
  if (!candles.length || period < 2) return [];
  const out = [];
  for (let i = period - 1; i < candles.length; i += 1) {
    const slice = candles.slice(i - period + 1, i + 1);
    const tps = slice.map(typicalPrice);
    const smaTp = tps.reduce((a, b) => a + b, 0) / period;
    const meanDev =
      tps.reduce((sum, tp) => sum + Math.abs(tp - smaTp), 0) / period;
    const tp = typicalPrice(candles[i]);
    const cci = meanDev > 0 ? (tp - smaTp) / (0.015 * meanDev) : 0;
    out.push({ time: candles[i].time, value: cci });
  }
  return out;
};

export const calculateWilliamsR = (candles, period) => {
  if (!candles.length || period < 2) return [];
  const out = [];
  for (let i = period - 1; i < candles.length; i += 1) {
    const slice = candles.slice(i - period + 1, i + 1);
    const hh = Math.max(...slice.map((c) => c.high));
    const ll = Math.min(...slice.map((c) => c.low));
    const c = candles[i].close;
    const denom = hh - ll;
    const wr = denom > 0 ? -100 * ((hh - c) / denom) : 0;
    out.push({ time: candles[i].time, value: wr });
  }
  return out;
};

export const calculateMFI = (candles, period) => {
  if (!candles.length || period < 2) return [];
  const rawFlows = [];
  for (let i = 1; i < candles.length; i += 1) {
    const tp = typicalPrice(candles[i]);
    const prevTp = typicalPrice(candles[i - 1]);
    const rmf = tp * candleVolume(candles[i]);
    const sign = tp > prevTp ? 1 : tp < prevTp ? -1 : 0;
    rawFlows.push({
      time: candles[i].time,
      pos: sign > 0 ? rmf : 0,
      neg: sign < 0 ? rmf : 0,
    });
  }
  if (rawFlows.length < period) return [];
  const out = [];
  let posSum = 0;
  let negSum = 0;
  for (let j = 0; j < period; j += 1) {
    posSum += rawFlows[j].pos;
    negSum += rawFlows[j].neg;
  }
  const pushMfi = (idx) => {
    const ratio = negSum === 0 ? 100 : posSum / negSum;
    const mfi = 100 - 100 / (1 + ratio);
    out.push({ time: rawFlows[idx].time, value: mfi });
  };
  pushMfi(period - 1);
  for (let j = period; j < rawFlows.length; j += 1) {
    posSum += rawFlows[j].pos - rawFlows[j - period].pos;
    negSum += rawFlows[j].neg - rawFlows[j - period].neg;
    pushMfi(j);
  }
  return out;
};

const DAY_SECONDS = 86400;

/** True when bars are finer than one day, i.e. VWAP should anchor per session.
 * Uses the smallest gap rather than the average so a weekend/holiday gap in an
 * intraday series doesn't push the estimate over a day. */
const isIntradaySeries = (candles) => {
  let smallestGap = Infinity;
  for (let i = 1; i < candles.length; i += 1) {
    const gap = candles[i].time - candles[i - 1].time;
    if (gap > 0 && gap < smallestGap) smallestGap = gap;
  }
  return smallestGap < DAY_SECONDS;
};

/** Volume-weighted average price.
 *
 * On intraday bars this anchors to each UTC day, the way every trading platform
 * computes session VWAP — a single cumulative run across weeks of bars flattens
 * out and drifts away from price, which makes the line useless for intraday.
 * On daily-or-coarser bars a per-day reset would put every bar in its own
 * session (VWAP == typical price), so those run cumulatively instead. */
export const calculateVWAP = (candles) => {
  if (!candles.length) return [];
  const perSession = isIntradaySeries(candles);
  let cumPv = 0;
  let cumV = 0;
  let sessionDay = null;
  const out = [];
  for (let i = 0; i < candles.length; i += 1) {
    const tp = typicalPrice(candles[i]);
    const v = candleVolume(candles[i]);
    if (perSession) {
      const day = Math.floor(candles[i].time / DAY_SECONDS);
      if (day !== sessionDay) {
        sessionDay = day;
        cumPv = 0;
        cumV = 0;
      }
    }
    cumPv += tp * v;
    cumV += v;
    out.push({
      time: candles[i].time,
      value: cumV > 0 ? cumPv / cumV : tp,
    });
  }
  return out;
};

export const calculateOBV = (candles) => {
  if (!candles.length) return [];
  const out = [{ time: candles[0].time, value: 0 }];
  let obv = 0;
  for (let i = 1; i < candles.length; i += 1) {
    const v = candleVolume(candles[i]);
    if (candles[i].close > candles[i - 1].close) obv += v;
    else if (candles[i].close < candles[i - 1].close) obv -= v;
    out.push({ time: candles[i].time, value: obv });
  }
  return out;
};

/** Wilder-smoothed +DI, -DI, and ADX */
export const calculateADX = (candles, period) => {
  const empty = { adx: [], plusDI: [], minusDI: [] };
  const p = period;
  const len = candles.length;
  const tr = [];
  const pdm = [];
  const mdm = [];
  for (let i = 1; i < len; i += 1) {
    const h = candles[i].high;
    const l = candles[i].low;
    const h0 = candles[i - 1].high;
    const l0 = candles[i - 1].low;
    const c0 = candles[i - 1].close;
    const up = h - h0;
    const down = l0 - l;
    pdm.push(up > down && up > 0 ? up : 0);
    mdm.push(down > up && down > 0 ? down : 0);
    tr.push(Math.max(h - l, Math.abs(h - c0), Math.abs(l - c0)));
  }
  const n = tr.length;
  if (n < p * 2) return empty;

  let smTr = 0;
  let smP = 0;
  let smM = 0;
  for (let i = 0; i < p; i += 1) {
    smTr += tr[i];
    smP += pdm[i];
    smM += mdm[i];
  }
  smTr /= p;
  smP /= p;
  smM /= p;

  const plusDI = [];
  const minusDI = [];
  const dxArr = [];

  for (let i = p - 1; i < n; i += 1) {
    if (i >= p) {
      smTr = (smTr * (p - 1) + tr[i]) / p;
      smP = (smP * (p - 1) + pdm[i]) / p;
      smM = (smM * (p - 1) + mdm[i]) / p;
    }
    const diP = smTr > 0 ? (100 * smP) / smTr : 0;
    const diM = smTr > 0 ? (100 * smM) / smTr : 0;
    const diSum = diP + diM;
    const dx = diSum > 0 ? (100 * Math.abs(diP - diM)) / diSum : 0;
    const time = candles[i + 1].time;
    plusDI.push({ time, value: diP });
    minusDI.push({ time, value: diM });
    dxArr.push(dx);
  }

  const adx = [];
  if (dxArr.length < p) return { adx, plusDI, minusDI };

  let adxSm = 0;
  for (let j = 0; j < p; j += 1) adxSm += dxArr[j];
  adxSm /= p;
  adx.push({ time: plusDI[p - 1].time, value: adxSm });

  for (let j = p; j < dxArr.length; j += 1) {
    adxSm = (adxSm * (p - 1) + dxArr[j]) / p;
    adx.push({ time: plusDI[j].time, value: adxSm });
  }

  return { adx, plusDI, minusDI };
};

export const calculateROC = (candles, period) => {
  if (!candles.length || period < 1) return [];
  const out = [];
  for (let i = period; i < candles.length; i += 1) {
    const prev = candles[i - period].close;
    const roc = prev !== 0 ? ((candles[i].close - prev) / prev) * 100 : 0;
    out.push({ time: candles[i].time, value: roc });
  }
  return out;
};

export const calculateCMF = (candles, period) => {
  if (!candles.length || period < 2) return [];
  const mfvs = [];
  for (let i = 0; i < candles.length; i += 1) {
    const c = candles[i];
    const range = c.high - c.low;
    const mfm =
      range > 0 ? ((c.close - c.low) - (c.high - c.close)) / range : 0;
    mfvs.push(mfm * candleVolume(c));
  }
  const out = [];
  let sumMfv = 0;
  let sumV = 0;
  for (let i = 0; i < period; i += 1) {
    sumMfv += mfvs[i];
    sumV += candleVolume(candles[i]);
  }
  for (let i = period - 1; i < candles.length; i += 1) {
    if (i >= period) {
      sumMfv += mfvs[i] - mfvs[i - period];
      sumV +=
        candleVolume(candles[i]) - candleVolume(candles[i - period]);
    }
    const cmf = sumV !== 0 ? sumMfv / sumV : 0;
    out.push({ time: candles[i].time, value: cmf });
  }
  return out;
};

const atrLineWilder = (candles, period) => {
  if (!candles.length || period < 1 || candles.length < period) return [];
  const trs = [];
  trs.push(candles[0].high - candles[0].low);
  for (let i = 1; i < candles.length; i += 1) {
    const c = candles[i];
    const p = candles[i - 1];
    const tr = Math.max(
      c.high - c.low,
      Math.abs(c.high - p.close),
      Math.abs(c.low - p.close)
    );
    trs.push(tr);
  }
  const out = [];
  let atr = 0;
  for (let i = 0; i < period; i += 1) atr += trs[i];
  atr /= period;
  out.push({ time: candles[period - 1].time, value: atr });
  for (let i = period; i < candles.length; i += 1) {
    atr = (atr * (period - 1) + trs[i]) / period;
    out.push({ time: candles[i].time, value: atr });
  }
  return out;
};

export const calculateKeltner = (candles, emaPeriod, atrPeriod, multiplier) => {
  if (!candles.length || emaPeriod < 1 || atrPeriod < 1) {
    return { upper: [], middle: [], lower: [] };
  }
  const asSeries = candles.map((c) => ({
    time: c.time,
    close: c.close,
  }));
  const middle = emaOfSeries(asSeries, emaPeriod);
  const atr = atrLineWilder(candles, atrPeriod);
  const atrByTime = new Map(atr.map((e) => [e.time, e.value]));
  const upper = [];
  const lower = [];
  for (const m of middle) {
    const a = atrByTime.get(m.time);
    if (!Number.isFinite(a)) continue;
    upper.push({
      time: m.time,
      value: m.value + multiplier * a,
    });
    lower.push({
      time: m.time,
      value: m.value - multiplier * a,
    });
  }
  return { upper, middle, lower };
};

export const calculateTRIX = (candles, period) => {
  if (!candles.length || period < 2) return [];
  const asSeries = candles.map((c) => ({ time: c.time, close: c.close }));
  const e1 = emaOfSeries(asSeries, period);
  const e1c = e1.map((e) => ({ time: e.time, close: e.value }));
  const e2 = emaOfSeries(e1c, period);
  const e2c = e2.map((e) => ({ time: e.time, close: e.value }));
  const e3 = emaOfSeries(e2c, period);
  const out = [];
  for (let i = 1; i < e3.length; i += 1) {
    const cur = e3[i].value;
    const prev = e3[i - 1].value;
    const trix = prev !== 0 ? ((cur - prev) / prev) * 10000 : 0;
    out.push({ time: e3[i].time, value: trix });
  }
  return out;
};

export const calculateAwesomeOscillator = (candles) => {
  const fast = 5;
  const slow = 34;
  if (candles.length < slow) return [];
  const median = (c) => (c.high + c.low) / 2;
  const out = [];
  for (let i = slow - 1; i < candles.length; i += 1) {
    let s5 = 0;
    for (let j = 0; j < fast; j += 1) {
      s5 += median(candles[i - j]);
    }
    let s34 = 0;
    for (let j = 0; j < slow; j += 1) {
      s34 += median(candles[i - j]);
    }
    const ao = s5 / fast - s34 / slow;
    out.push({ time: candles[i].time, value: ao });
  }
  return out;
};

export const calculateDPO = (candles, period) => {
  if (!candles.length || period < 3) return [];
  const sma = (endIdx) => {
    let s = 0;
    for (let j = 0; j < period; j += 1) {
      s += candles[endIdx - j].close;
    }
    return s / period;
  };
  const shift = Math.floor(period / 2) + 1;
  const out = [];
  for (let i = period - 1 + shift; i < candles.length; i += 1) {
    const smaNow = sma(i);
    const pastClose = candles[i - shift].close;
    out.push({ time: candles[i].time, value: pastClose - smaNow });
  }
  return out;
};

const wmaOnValues = (points, period) => {
  if (!points.length || period < 1) return [];
  const weightSum = (period * (period + 1)) / 2;
  return points.flatMap((pt, index) => {
    if (index < period - 1) return [];
    const slice = points.slice(index - period + 1, index + 1);
    const weightedTotal = slice.reduce(
      (sum, item, windowIndex) => sum + item.value * (windowIndex + 1),
      0
    );
    return [{ time: pt.time, value: weightedTotal / weightSum }];
  });
};

/** Hull Moving Average */
export const calculateHMA = (candles, period) => {
  if (!candles.length || period < 2) return [];
  const half = Math.max(1, Math.floor(period / 2));
  const sqrtP = Math.max(1, Math.floor(Math.sqrt(period)));
  const wmaHalf = calculateWMA(candles, half);
  const wmaFull = calculateWMA(candles, period);
  const wHalf = new Map(wmaHalf.map((e) => [e.time, e.value]));
  const raw = [];
  for (const wf of wmaFull) {
    const wh = wHalf.get(wf.time);
    if (!Number.isFinite(wh)) continue;
    raw.push({ time: wf.time, value: 2 * wh - wf.value });
  }
  return wmaOnValues(raw, sqrtP);
};

export const calculateDEMA = (candles, period) => {
  if (!candles.length || period < 1) return [];
  const e1 = calculateEMA(candles, period);
  const e1AsCandles = e1.map((e) => ({ time: e.time, open: e.value, high: e.value, low: e.value, close: e.value }));
  const e2 = calculateEMA(e1AsCandles, period);
  const e2ByTime = new Map(e2.map((e) => [e.time, e.value]));
  return e1.flatMap((e) => {
    const v = e2ByTime.get(e.time);
    if (!Number.isFinite(v)) return [];
    return [{ time: e.time, value: 2 * e.value - v }];
  });
};

export const calculateTEMA = (candles, period) => {
  if (!candles.length || period < 1) return [];
  const e1 = calculateEMA(candles, period);
  const c1 = e1.map((e) => ({ time: e.time, open: e.value, high: e.value, low: e.value, close: e.value }));
  const e2 = calculateEMA(c1, period);
  const c2 = e2.map((e) => ({ time: e.time, open: e.value, high: e.value, low: e.value, close: e.value }));
  const e3 = calculateEMA(c2, period);
  const m2 = new Map(e2.map((e) => [e.time, e.value]));
  const m3 = new Map(e3.map((e) => [e.time, e.value]));
  return e1.flatMap((e) => {
    const v2 = m2.get(e.time);
    const v3 = m3.get(e.time);
    if (!Number.isFinite(v2) || !Number.isFinite(v3)) return [];
    return [{ time: e.time, value: 3 * e.value - 3 * v2 + v3 }];
  });
};

/** Ichimoku lines (Senkou spans shifted forward on the time axis). */
export const calculateIchimoku = (
  candles,
  tenkanPeriod = 9,
  kijunPeriod = 26,
  senkouBPeriod = 52,
  displacement = 26
) => {
  const n = candles.length;
  if (n < Math.max(tenkanPeriod, kijunPeriod, senkouBPeriod) + displacement) {
    return {
      tenkan: [],
      kijun: [],
      spanA: [],
      spanB: [],
      chikou: [],
    };
  }
  const midRange = (from, len) => {
    let hh = -Infinity;
    let ll = Infinity;
    for (let j = 0; j < len; j += 1) {
      const c = candles[from + j];
      hh = Math.max(hh, c.high);
      ll = Math.min(ll, c.low);
    }
    return (hh + ll) / 2;
  };
  const tenkan = [];
  const kijun = [];
  for (let i = tenkanPeriod - 1; i < n; i += 1) {
    tenkan.push({
      time: candles[i].time,
      value: midRange(i - tenkanPeriod + 1, tenkanPeriod),
    });
  }
  for (let i = kijunPeriod - 1; i < n; i += 1) {
    kijun.push({
      time: candles[i].time,
      value: midRange(i - kijunPeriod + 1, kijunPeriod),
    });
  }
  const tenkanByTime = new Map(tenkan.map((e) => [e.time, e.value]));
  const kijunByTime = new Map(kijun.map((e) => [e.time, e.value]));
  const spanA = [];
  const spanB = [];
  for (let i = 0; i < n; i += 1) {
    const t = tenkanByTime.get(candles[i].time);
    const k = kijunByTime.get(candles[i].time);
    if (Number.isFinite(t) && Number.isFinite(k) && i + displacement < n) {
      spanA.push({
        time: candles[i + displacement].time,
        value: (t + k) / 2,
      });
    }
    if (i >= senkouBPeriod - 1 && i + displacement < n) {
      spanB.push({
        time: candles[i + displacement].time,
        value: midRange(i - senkouBPeriod + 1, senkouBPeriod),
      });
    }
  }
  const chikou = [];
  for (let i = displacement; i < n; i += 1) {
    chikou.push({
      time: candles[i].time,
      value: candles[i - displacement].close,
    });
  }
  return { tenkan, kijun, spanA, spanB, chikou };
};

/** Parabolic SAR (Wilder-style). */
export const calculateParabolicSAR = (candles, step = 0.02, maxStep = 0.2) => {
  if (candles.length < 2) return [];
  const out = [];
  let isLong = candles[1].close >= candles[0].close;
  let af = step;
  let ep = isLong ? candles[0].high : candles[0].low;
  let sar = isLong ? Math.min(candles[0].low, candles[1].low) : Math.max(candles[0].high, candles[1].high);
  out.push({ time: candles[0].time, value: sar });
  for (let i = 1; i < candles.length; i += 1) {
    const c = candles[i];
    const prevC = candles[i - 1];
    sar = sar + af * (ep - sar);
    if (isLong) {
      if (c.low < sar) {
        isLong = false;
        sar = ep;
        ep = c.low;
        af = step;
      } else {
        if (c.high > ep) {
          ep = c.high;
          af = Math.min(af + step, maxStep);
        }
        sar = Math.min(sar, prevC.low, i > 1 ? candles[i - 2].low : prevC.low);
      }
    } else {
      if (c.high > sar) {
        isLong = true;
        sar = ep;
        ep = c.high;
        af = step;
      } else {
        if (c.low < ep) {
          ep = c.low;
          af = Math.min(af + step, maxStep);
        }
        sar = Math.max(sar, prevC.high, i > 1 ? candles[i - 2].high : prevC.high);
      }
    }
    out.push({ time: c.time, value: sar });
  }
  return out;
};

export const calculateStochRSI = (
  candles,
  rsiPeriod,
  stochPeriod,
  kSmooth,
  dSmooth
) => {
  const rsiPts = calculateRSI(candles, rsiPeriod);
  if (rsiPts.length < stochPeriod) return { k: [], d: [] };
  const rsiAsCandles = rsiPts.map((e) => ({
    time: e.time,
    open: e.value,
    high: e.value,
    low: e.value,
    close: e.value,
  }));
  const hi = (from, len) => {
    let h = -Infinity;
    for (let j = 0; j < len; j += 1) h = Math.max(h, rsiAsCandles[from + j].close);
    return h;
  };
  const lo = (from, len) => {
    let l = Infinity;
    for (let j = 0; j < len; j += 1) l = Math.min(l, rsiAsCandles[from + j].close);
    return l;
  };
  const rawK = [];
  for (let i = stochPeriod - 1; i < rsiAsCandles.length; i += 1) {
    const hh = hi(i - stochPeriod + 1, stochPeriod);
    const ll = lo(i - stochPeriod + 1, stochPeriod);
    const cl = rsiAsCandles[i].close;
    const denom = hh - ll;
    const st = denom === 0 ? 50 : ((cl - ll) / denom) * 100;
    rawK.push({ time: rsiAsCandles[i].time, value: st });
  }
  const kSm =
    kSmooth > 1
      ? calculateSMA(
          rawK.map((e) => ({
            time: e.time,
            open: e.value,
            high: e.value,
            low: e.value,
            close: e.value,
          })),
          kSmooth
        )
      : rawK;
  const dSm =
    dSmooth > 1 && kSm.length >= dSmooth
      ? calculateSMA(
          kSm.map((e) => ({
            time: e.time,
            open: e.value,
            high: e.value,
            low: e.value,
            close: e.value,
          })),
          dSmooth
        )
      : kSm;
  return { k: kSm, d: dSm };
};

export const calculateTSI = (candles, longPeriod = 25, shortPeriod = 13) => {
  if (candles.length < longPeriod + shortPeriod) return [];
  const pc = [];
  for (let i = 1; i < candles.length; i += 1) {
    const change = candles[i].close - candles[i - 1].close;
    pc.push({
      time: candles[i].time,
      close: change,
    });
  }
  const apc = pc.map((e) => ({
    time: e.time,
    close: Math.abs(e.close),
  }));
  const e1 = emaOfSeries(pc, longPeriod);
  const e1c = e1.map((e) => ({ time: e.time, close: e.value }));
  const num = emaOfSeries(e1c, shortPeriod);
  const e2 = emaOfSeries(apc, longPeriod);
  const e2c = e2.map((e) => ({ time: e.time, close: e.value }));
  const den = emaOfSeries(e2c, shortPeriod);
  const denByTime = new Map(den.map((e) => [e.time, e.value]));
  return num.flatMap((e) => {
    const d = denByTime.get(e.time);
    if (!Number.isFinite(d) || d === 0) return [];
    return [{ time: e.time, value: 100 * (e.value / d) }];
  });
};

/** Rolling standard deviation of close (line, same window as SMA). */
export const calculateRollingStdDev = (candles, period) => {
  if (!candles.length || period < 2) return [];
  const out = [];
  for (let index = period - 1; index < candles.length; index += 1) {
    const window = candles.slice(index - period + 1, index + 1);
    const mean = window.reduce((s, c) => s + c.close, 0) / period;
    const variance =
      window.reduce((s, c) => s + (c.close - mean) ** 2, 0) / period;
    out.push({ time: candles[index].time, value: Math.sqrt(variance) });
  }
  return out;
};

export const calculateChaikinVolatility = (candles, emaPeriod = 10, rocPeriod = 10) => {
  if (!candles.length || emaPeriod < 1 || rocPeriod < 1) return [];
  const hl = candles.map((c) => ({
    time: c.time,
    close: c.high - c.low,
  }));
  const emaHl = emaOfSeries(hl, emaPeriod);
  const out = [];
  for (let i = rocPeriod; i < emaHl.length; i += 1) {
    const cur = emaHl[i].value;
    const prev = emaHl[i - rocPeriod].value;
    const cv = prev !== 0 ? (100 * (cur - prev)) / prev : 0;
    out.push({ time: emaHl[i].time, value: cv });
  }
  return out;
};

export const calculateAccumulationDistribution = (candles) => {
  if (!candles.length) return [];
  let ad = 0;
  const out = [];
  for (let i = 0; i < candles.length; i += 1) {
    const c = candles[i];
    const range = c.high - c.low;
    const mfm = range > 0 ? ((c.close - c.low) - (c.high - c.close)) / range : 0;
    ad += mfm * candleVolume(c);
    out.push({ time: c.time, value: ad });
  }
  return out;
};

export const calculateVolumeOscillator = (candles, fastPeriod = 5, slowPeriod = 10) => {
  if (!candles.length || slowPeriod < 1 || fastPeriod >= slowPeriod) return [];
  const vols = candles.map((c) => ({
    time: c.time,
    close: candleVolume(c),
  }));
  const fastMa = emaOfSeries(vols, fastPeriod);
  const slowMa = emaOfSeries(vols, slowPeriod);
  const slowByTime = new Map(slowMa.map((e) => [e.time, e.value]));
  return fastMa.flatMap((e) => {
    const s = slowByTime.get(e.time);
    if (!Number.isFinite(s) || s === 0) return [];
    return [{ time: e.time, value: ((e.value - s) / s) * 100 }];
  });
};

export const calculateMomentum = (candles, period) => {
  if (!candles.length || period < 1) return [];
  const out = [];
  for (let i = period; i < candles.length; i += 1) {
    out.push({
      time: candles[i].time,
      value: candles[i].close - candles[i - period].close,
    });
  }
  return out;
};

/**
 * Kaufman Efficiency Ratio (0..1): net displacement over `period` bars divided by the
 * total distance travelled. ~1 means price moved efficiently in one direction
 * (trending); ~0 means price churned back and forth (mean-reverting/choppy). Forward-
 * fills through degenerate zero-path bars and EMA-smooths (span = `smooth`).
 */
const kaufmanEfficiencyRatioRaw = (candles, period, smooth) => {
  const n = candles.length;
  const closes = candles.map((c) => c.close);
  const pathSum = new Array(n).fill(NaN);
  let rollingPath = 0;
  for (let i = 1; i < n; i += 1) {
    rollingPath += Math.abs(closes[i] - closes[i - 1]);
    if (i > period) rollingPath -= Math.abs(closes[i - period] - closes[i - period - 1]);
    if (i >= period) pathSum[i] = rollingPath;
  }

  let lastEr = 0.5;
  const er = new Array(n).fill(0.5);
  for (let i = 0; i < n; i += 1) {
    if (i >= period && Number.isFinite(pathSum[i]) && pathSum[i] > 0) {
      const disp = Math.abs(closes[i] - closes[i - period]);
      lastEr = Math.min(Math.max(disp / pathSum[i], 0), 1);
    }
    er[i] = lastEr;
  }

  const alpha = smooth > 1 ? 2 / (smooth + 1) : 1;
  const smoothed = new Array(n);
  let smoothedEr = er[0];
  for (let i = 0; i < n; i += 1) {
    smoothedEr = i === 0 ? er[i] : alpha * er[i] + (1 - alpha) * smoothedEr;
    smoothed[i] = Math.min(Math.max(smoothedEr, 0), 1);
  }
  return smoothed;
};

/**
 * Kaufman Range Detection: the raw Efficiency Ratio (0..1) as a standalone oscillator.
 * High values (toward 1) flag an efficiently trending market; low values (toward 0)
 * flag a ranging/choppy market. Skips the warmup window rather than padding it, like
 * the other oscillator indicators (calculateCCI, calculateWilliamsR, etc.).
 */
export const calculateKaufmanEfficiencyRatio = (candles, period = 20, smooth = 5) => {
  if (!candles.length || period < 1) return [];
  const raw = kaufmanEfficiencyRatioRaw(candles, period, smooth);
  const out = [];
  for (let i = period; i < candles.length; i += 1) {
    out.push({ time: candles[i].time, value: raw[i] });
  }
  return out;
};

